import React, { useEffect, useState, useRef, useContext } from 'react';
import { Loader2, Share2, Trash2, ChevronRight, Menu, Settings, X } from 'lucide-react';
import { semanticBrandingService } from '../../services/semanticBranding';
import { ToastContext } from '../../App';
import { useNavigate } from 'react-router-dom';
import AnalysisProgressBar from './AnalysisProgressBar';
import ScoreBarChart from './AnalysisCharts/ScoreBarChart';
import RadarChart from './AnalysisCharts/RadarChart';
import AttributeTopBrands from './AnalysisCharts/AttributeTopBrands';
import ScoreHeatmap from './AnalysisCharts/ScoreHeatmap';
import JustificativasSection from './AnalysisCharts/JustificativasSection';
import ShareModal from './ShareModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import SettingsModal from './SettingsModal';
interface FinalReportStepProps {
  documentId: string;
  onPreviousStep: () => void;
}
const FinalReportStep: React.FC<FinalReportStepProps> = ({
  documentId,
  onPreviousStep
}) => {
  const {
    addToast
  } = useContext(ToastContext);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [competitors, setCompetitors] = useState<any[]>([]);
  const [activeSection, setActiveSection] = useState('score-overview');
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isNavFixed, setIsNavFixed] = useState(false);
  // Refs for sections and sidebar
  const sidebarRef = useRef<HTMLDivElement>(null);
  const sidebarContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scoreOverviewRef = useRef<HTMLDivElement>(null);
  const radarChartRef = useRef<HTMLDivElement>(null);
  const topBrandsRef = useRef<HTMLDivElement>(null);
  const heatmapRef = useRef<HTMLDivElement>(null);
  const justificativasRef = useRef<HTMLDivElement>(null);
  // Load analysis data or start analysis if not available
  useEffect(() => {
    const loadAnalysisData = async () => {
      setIsLoading(true);
      setError('');
      try {
        console.log('Loading analysis data for document:', documentId);
        const response = await semanticBrandingService.getBrandDocument(documentId);
        console.log('Received response:', response);
        const {
          brandDocument
        } = response;
        setIsPublic(brandDocument.visibility === 'public');
        // Store competitors for favicon lookup
        if (brandDocument.metadata?.concorrentesSelecionados) {
          setCompetitors(brandDocument.metadata.concorrentesSelecionados);
        }
        // Log completo do documento para depuração
        console.log('Full document:', brandDocument);
        // Verificação ampliada para detectar análise concluída
        if ((brandDocument.step === 'analise' || brandDocument.step === 'concluido') && brandDocument.metadata?.analiseComparativa) {
          // Analysis already exists
          console.log('Analysis data found in response');
          // Extract scores directly from analiseComparativa
          const scores = brandDocument.metadata.analiseComparativa || {};
          // Calculate overall scores by averaging attribute scores for each brand
          const scoreGeral = {};
          Object.keys(scores).forEach(brand => {
            const brandScores = scores[brand];
            const total = Object.values(brandScores).reduce((sum: any, score: any) => {
              // Handle both old and new format
              const scoreValue = typeof score === 'number' ? score : score && typeof score === 'object' && 'score' in score ? score.score : 0;
              return sum + scoreValue;
            }, 0);
            scoreGeral[brand] = Math.round(total / Object.keys(brandScores).length);
          });
          // Get attributes from the correct field
          const attributes = brandDocument.metadata.atributos || [];
          // Set analysis data with the correct structure
          setAnalysisData({
            scores: scores,
            scoreGeral: scoreGeral,
            insights: brandDocument.metadata.analiseComparativa.insights || 'Não foram encontrados insights para esta análise.',
            sugestoes: brandDocument.metadata.analiseComparativa.sugestoes || [],
            pontosFortes: brandDocument.metadata.analiseComparativa.pontosFortes || [],
            oportunidades: brandDocument.metadata.analiseComparativa.oportunidades || [],
            attributes: attributes,
            marca: brandDocument.marca,
            url: brandDocument.url,
            metadata: brandDocument.metadata,
            createdAt: brandDocument.createdAt,
            updatedAt: brandDocument.updatedAt
          });
        } else if (brandDocument.ultimaAnalise && brandDocument.metadata?.analiseComparativa) {
          // Análise existe com base na data da última análise
          console.log('Analysis data found based on ultimaAnalise date');
          // Mesmo código para extrair dados
          const scores = brandDocument.metadata.analiseComparativa || {};
          const scoreGeral = {};
          Object.keys(scores).forEach(brand => {
            const brandScores = scores[brand];
            const total = Object.values(brandScores).reduce((sum: any, score: any) => {
              const scoreValue = typeof score === 'number' ? score : score && typeof score === 'object' && 'score' in score ? score.score : 0;
              return sum + scoreValue;
            }, 0);
            scoreGeral[brand] = Math.round(total / Object.keys(brandScores).length);
          });
          const attributes = brandDocument.metadata.atributos || [];
          setAnalysisData({
            scores: scores,
            scoreGeral: scoreGeral,
            insights: brandDocument.metadata.analiseComparativa.insights || 'Não foram encontrados insights para esta análise.',
            sugestoes: brandDocument.metadata.analiseComparativa.sugestoes || [],
            pontosFortes: brandDocument.metadata.analiseComparativa.pontosFortes || [],
            oportunidades: brandDocument.metadata.analiseComparativa.oportunidades || [],
            attributes: attributes,
            marca: brandDocument.marca,
            url: brandDocument.url,
            metadata: brandDocument.metadata,
            createdAt: brandDocument.createdAt,
            updatedAt: brandDocument.updatedAt
          });
        } else {
          // Need to run analysis
          console.log('No analysis data found, will start analysis');
          setIsAnalyzing(true);
          // Iniciar a análise imediatamente em vez de esperar a barra de progresso
          try {
            console.log('Iniciando análise imediatamente para o documento:', documentId);
            // Chamar o endpoint para iniciar a análise
            await semanticBrandingService.runAnalysis(documentId, true);
            console.log('Solicitação de análise enviada com sucesso');
          } catch (error) {
            console.error('Erro ao iniciar análise:', error);
            // Não definimos erro aqui, pois a análise ainda pode estar em andamento
            // e o AnalysisProgressBar irá verificar o status
          }
        }
      } catch (error) {
        console.error('Failed to load analysis data:', error);
        setError(error.response?.data?.message || 'Erro ao carregar dados da análise. Tente novamente.');
      } finally {
        setIsLoading(false);
      }
    };
    loadAnalysisData();
  }, [documentId]);
  // Handle scroll and fix sidebar when needed
  useEffect(() => {
    const handleScroll = () => {
      if (!sidebarContainerRef.current) return;
      const sidebarContainer = sidebarContainerRef.current;
      const containerRect = sidebarContainer.getBoundingClientRect();
      // Check if sidebar should be fixed (container top reaches viewport top)
      if (containerRect.top <= 0) {
        setIsNavFixed(true);
      } else {
        setIsNavFixed(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  // Set up scroll observation for section tracking
  useEffect(() => {
    if (!isLoading && !isAnalyzing && analysisData) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      }, {
        rootMargin: '-100px 0px -60% 0px',
        threshold: 0.1
      });
      const sections = [scoreOverviewRef.current, radarChartRef.current, topBrandsRef.current, heatmapRef.current, justificativasRef.current];
      sections.forEach(section => {
        if (section) observer.observe(section);
      });
      return () => {
        sections.forEach(section => {
          if (section) observer.unobserve(section);
        });
      };
    }
  }, [isLoading, isAnalyzing, analysisData]);
  // Run analysis when isAnalyzing is true and timer completes
  const handleAnalysisComplete = async () => {
    try {
      console.log('Verificando se a análise foi concluída para o documento:', documentId);
      // Em vez de iniciar a análise aqui, apenas verificamos se ela foi concluída
      const response = await semanticBrandingService.getBrandDocument(documentId);
      console.log('Resposta da verificação de análise:', response);
      // Change const to let to allow reassignment
      let {
        brandDocument
      } = response;
      // Verificar se a análise foi concluída
      if (brandDocument.step !== 'concluido' && (!brandDocument.metadata?.analiseComparativa || Object.keys(brandDocument.metadata.analiseComparativa).length === 0)) {
        console.log('Análise ainda não foi concluída, tentando novamente...');
        // Se a análise ainda não foi concluída, podemos tentar novamente
        const retryResponse = await semanticBrandingService.runAnalysis(documentId, true);
        console.log('Nova tentativa de análise:', retryResponse);
        brandDocument = retryResponse.brandDocument;
      }
      // Extract scores directly from analiseComparativa
      const scores = brandDocument.metadata?.analiseComparativa || {};
      // Calculate overall scores by averaging attribute scores for each brand
      const scoreGeral = {};
      Object.keys(scores).forEach(brand => {
        if (typeof scores[brand] === 'object') {
          const brandScores = scores[brand];
          const total = Object.values(brandScores).reduce((sum: any, score: any) => {
            // Handle both old and new format
            const scoreValue = typeof score === 'number' ? score : score && typeof score === 'object' && 'score' in score ? score.score : 0;
            return sum + scoreValue;
          }, 0);
          scoreGeral[brand] = Math.round(total / Object.keys(brandScores).length);
        }
      });
      // Get attributes from the correct field
      const attributes = brandDocument.metadata?.atributos || [];
      setAnalysisData({
        scores: scores,
        scoreGeral: scoreGeral,
        insights: brandDocument.metadata?.analiseComparativa?.insights || 'Não foram encontrados insights para esta análise.',
        sugestoes: brandDocument.metadata?.analiseComparativa?.sugestoes || [],
        pontosFortes: brandDocument.metadata?.analiseComparativa?.pontosFortes || [],
        oportunidades: brandDocument.metadata?.analiseComparativa?.oportunidades || [],
        attributes: attributes,
        marca: brandDocument.marca,
        url: brandDocument.url,
        metadata: brandDocument.metadata,
        createdAt: brandDocument.createdAt,
        updatedAt: brandDocument.updatedAt
      });
      addToast('success', 'Análise semântica concluída com sucesso!');
    } catch (error) {
      console.error('Failed to run analysis:', error);
      setError(error.response?.data?.message || 'Erro ao executar a análise. Tente novamente.');
    } finally {
      setIsAnalyzing(false);
    }
  };
  // Get favicon from URL
  const getFavicon = (url: string) => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
    } catch (e) {
      return `https://www.google.com/s2/favicons?domain=example.com&sz=128`;
    }
  };
  // Handle visibility change
  const handleVisibilityChange = (newIsPublic: boolean) => {
    setIsPublic(newIsPublic);
    addToast('success', newIsPublic ? 'Relatório agora é público e pode ser compartilhado' : 'Relatório agora é privado');
  };
  // Handle document deletion
  const handleDeleteDocument = async () => {
    try {
      await semanticBrandingService.deleteBrandDocument(documentId);
      addToast('success', 'Relatório excluído com sucesso');
      navigate('/');
    } catch (error) {
      console.error('Failed to delete document:', error);
      addToast('error', error.response?.data?.message || 'Erro ao excluir relatório. Tente novamente.');
      setIsDeleteModalOpen(false);
    }
  };
  // Find competitor URL by brand name
  const findCompetitorUrl = (brandName: string) => {
    if (brandName === analysisData?.marca) {
      return analysisData?.url || '';
    }
    const competitor = competitors.find(comp => comp.marca === brandName);
    return competitor?.url || '';
  };
  // Scroll to section
  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({
        behavior: 'smooth'
      });
      // Close mobile nav after clicking
      setIsMobileNavOpen(false);
    }
  };
  if (isLoading) {
    return <div className="w-full" data-id="element-1065">
        <div className="flex justify-center items-center py-12" data-id="element-1066">
          <Loader2 size={32} className="animate-spin text-[#1649ff] mr-3" data-id="element-1067" />
          <span className="text-lg text-gray-900" data-id="element-1068">Carregando relatório...</span>
        </div>
      </div>;
  }
  if (error) {
    return <div className="w-full" data-id="element-1069">
        <div className="p-6 bg-red-50 rounded-md text-red-700" data-id="element-1070">
          <h3 className="text-lg font-semibold mb-2" data-id="element-1071">Erro</h3>
          <p data-id="element-1072">{error}</p>
        </div>
      </div>;
  }
  if (isAnalyzing) {
    return <div className="w-full" data-id="element-1073">
        <h2 className="text-xl font-semibold mb-6 text-gray-900" data-id="element-1074">
          Análise Semântica
        </h2>
        <AnalysisProgressBar isAnalyzing={isAnalyzing} onComplete={handleAnalysisComplete} documentId={documentId} data-id="element-1075" />
        <div className="p-8 bg-gray-50 rounded-md flex flex-col items-center justify-center" data-id="element-1076">
          <Loader2 size={32} className="animate-spin text-[#1649ff] mb-4" data-id="element-1077" />
          <p className="text-gray-900 text-center" data-id="element-1078">
            Processando análise semântica da sua marca e concorrentes.
            <br data-id="element-1079" />
            Este processo pode levar até 3 minutos.
          </p>
        </div>
      </div>;
  }
  if (!analysisData) {
    return <div className="w-full" data-id="element-1080">
        <div className="p-6 bg-yellow-50 rounded-md text-yellow-700" data-id="element-1081">
          <h3 className="text-lg font-semibold mb-2" data-id="element-1082">Dados não encontrados</h3>
          <p data-id="element-1083">Não foi possível encontrar os dados da análise.</p>
        </div>
      </div>;
  }
  // Navigation items
  const navItems = [{
    id: 'score-overview',
    label: 'Score por Marca'
  }, {
    id: 'radar-chart',
    label: 'Radar Comparativo'
  }, {
    id: 'top-brands',
    label: 'Top Marcas por Atributo'
  }, {
    id: 'score-heatmap',
    label: 'Heatmap de Scores'
  }, {
    id: 'justificativas',
    label: 'Justificativas das Notas'
  }];
  return <div className="w-full" data-id="element-1084">
      {/* Mobile Navigation Toggle */}
      <div className="lg:hidden flex justify-between items-center mb-4 border-b pb-3" data-id="element-1085">
        <h2 className="text-lg font-semibold" data-id="element-1086">Navegação</h2>
        <button onClick={() => setIsMobileNavOpen(!isMobileNavOpen)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-md" data-id="element-1087">
          {isMobileNavOpen ? <X size={20} data-id="element-1088" /> : <Menu size={20} data-id="element-1089" />}
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8" data-id="element-1090">
        {/* Sidebar Container - this is the reference for scroll positioning */}
        <div ref={sidebarContainerRef} className="lg:w-64 flex-shrink-0 relative" data-id="element-1091">
          {/* Sidebar Navigation - Desktop */}
          <div ref={sidebarRef} className={`
              lg:block w-full bg-white rounded-lg
              ${isMobileNavOpen ? 'block' : 'hidden'} 
              ${isNavFixed ? 'lg:fixed lg:top-4 lg:w-64' : 'lg:relative'}
            `} style={{
          maxHeight: isNavFixed ? 'calc(100vh - 32px)' : 'auto',
          overflowY: isNavFixed ? 'auto' : 'visible',
          zIndex: 10
        }} data-id="element-1092">
            <div className="flex flex-col h-full p-4 rounded-lg shadow-sm" data-id="element-1093">
              <div className="flex-grow" data-id="element-1094">
                <h3 className="text-lg font-semibold mb-4 hidden lg:block text-gray-900" data-id="element-1095">
                  Navegação
                </h3>
                <nav className="space-y-1 mb-6" data-id="element-1096">
                  {navItems.map(item => <button key={item.id} onClick={() => scrollToSection(item.id)} className={`w-full text-left px-3 py-2 rounded-md flex items-center ${activeSection === item.id ? 'bg-blue-50 text-[#1649ff] font-medium' : 'text-gray-900 hover:bg-gray-50'}`} data-id="element-1097">
                      <span data-id="element-1098">{item.label}</span>
                      {activeSection === item.id && <ChevronRight size={16} className="ml-auto" data-id="element-1099" />}
                    </button>)}
                </nav>
                <div className="space-y-3" data-id="element-1100">
                  <button onClick={() => setIsShareModalOpen(true)} className="w-full px-4 py-2 bg-[#1649ff] text-white rounded-md hover:bg-blue-700 flex items-center justify-center" data-id="element-1101">
                    <Share2 size={16} className="mr-2" data-id="element-1102" />
                    Compartilhar
                  </button>
                </div>
              </div>
              {/* Settings button at the bottom */}
              <div className="mt-auto pt-6 border-t border-gray-100" data-id="element-1103">
                <button onClick={() => setIsSettingsModalOpen(true)} className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center justify-center" data-id="element-1104">
                  <Settings size={16} className="mr-2" data-id="element-1105" />
                  Configurações
                </button>
              </div>
            </div>
          </div>
          {/* This creates a placeholder space when the sidebar is fixed */}
          {isNavFixed && <div className="hidden lg:block w-64 flex-shrink-0" aria-hidden="true" data-id="element-1106"></div>}
        </div>

        {/* Main Content */}
        <div ref={contentRef} className="flex-grow w-full max-w-full overflow-x-hidden" data-id="element-1107">
          {/* Report Header */}
          <div className="mb-6" data-id="element-1108">
            <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-gray-900" data-id="element-1109">
              Análise Semântica: {analysisData.marca}
            </h1>
            <p className="text-gray-800" data-id="element-1110">
              Relatório comparativo da sua marca em relação aos concorrentes com
              base em atributos semânticos.
            </p>
          </div>

          {/* Charts and visualizations */}
          <div className="space-y-12 sm:space-y-16" data-id="element-1111">
            {/* Section 1: Bar Chart - Score by Brand */}
            <section id="score-overview" ref={scoreOverviewRef} className="scroll-mt-24" data-id="element-1112">
              <h2 className="text-xl sm:text-2xl font-bold mb-3 text-gray-900" data-id="element-1113">
                Score por Marca
              </h2>
              <p className="text-gray-800 mb-6" data-id="element-1114">
                Visão geral da pontuação média de cada marca, calculada com base
                em todos os atributos analisados.
              </p>
              <div className="overflow-x-auto" data-id="element-1115">
                <ScoreBarChart scores={analysisData.scoreGeral} mainBrand={analysisData.marca} findCompetitorUrl={findCompetitorUrl} getFavicon={getFavicon} data-id="element-1116" />
              </div>
            </section>

            {/* Section 2: Radar Chart - Brand vs Competitors */}
            <section id="radar-chart" ref={radarChartRef} className="scroll-mt-24" data-id="element-1117">
              <h2 className="text-xl sm:text-2xl font-bold mb-3 text-gray-900" data-id="element-1118">
                Radar Comparativo
              </h2>
              <p className="text-gray-800 mb-6" data-id="element-1119">
                Comparação visual dos atributos da sua marca em relação aos
                concorrentes selecionados.
              </p>
              <div data-id="element-1120">
                <RadarChart scores={analysisData.scores} mainBrand={analysisData.marca} attributes={analysisData.attributes} allowMainBrandDeselection={true} data-id="element-1121" />
              </div>
            </section>

            {/* Section 3: Top 5 Brands by Attribute */}
            <section id="top-brands" ref={topBrandsRef} className="scroll-mt-24" data-id="element-1122">
              <h2 className="text-xl sm:text-2xl font-bold mb-3 text-gray-900" data-id="element-1123">
                Top Marcas por Atributo
              </h2>
              <p className="text-gray-800 mb-6" data-id="element-1124">
                As marcas com melhor desempenho em cada atributo analisado.
              </p>
              <div data-id="element-1125">
                <AttributeTopBrands scores={analysisData.scores} attributes={analysisData.attributes} findCompetitorUrl={findCompetitorUrl} getFavicon={getFavicon} data-id="element-1126" />
              </div>
            </section>

            {/* Section 4: Heatmap - Score by Brand and Attribute */}
            <section id="score-heatmap" ref={heatmapRef} className="scroll-mt-24" data-id="element-1127">
              <h2 className="text-xl sm:text-2xl font-bold mb-3 text-gray-900" data-id="element-1128">
                Heatmap de Scores
              </h2>
              <p className="text-gray-800 mb-6" data-id="element-1129">
                Visualização detalhada das pontuações de cada marca em todos os
                atributos analisados.
              </p>
              <div data-id="element-1130">
                <ScoreHeatmap scores={analysisData.scores} scoreGeral={analysisData.scoreGeral} mainBrand={analysisData.marca} attributes={analysisData.attributes} findCompetitorUrl={findCompetitorUrl} getFavicon={getFavicon} data-id="element-1131" />
              </div>
            </section>

            {/* Section 5: Justificativas - Score Justifications */}
            <section id="justificativas" ref={justificativasRef} className="scroll-mt-24" data-id="element-1132">
              <JustificativasSection scores={analysisData.scores} attributes={analysisData.attributes} mainBrand={analysisData.marca} findCompetitorUrl={findCompetitorUrl} getFavicon={getFavicon} data-id="element-1133" />
            </section>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ShareModal isOpen={isShareModalOpen} onClose={() => setIsShareModalOpen(false)} documentId={documentId} isPublic={isPublic} onVisibilityChange={handleVisibilityChange} data-id="element-1134" />
      <DeleteConfirmationModal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} onConfirm={handleDeleteDocument} documentName={analysisData.marca} data-id="element-1135" />
      <SettingsModal isOpen={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)} onDeleteClick={() => {
      setIsSettingsModalOpen(false);
      setIsDeleteModalOpen(true);
    }} analysisData={analysisData} data-id="element-1136" />
    </div>;
};
export default FinalReportStep;